import { isValidPlacement } from "./validator";
/**
 * Finds the empty cell with the fewest legal candidates (MRV heuristic).
 * Returns -1 if no empty cells remain.
 */
function nextCell(grid) {
    let bestCell = -1;
    let bestCount = 10;
    for (let i = 0; i < 81; i++) {
        if (grid[i] !== 0)
            continue;
        const row = Math.floor(i / 9);
        const col = i % 9;
        let count = 0;
        for (let v = 1; v <= 9; v++) {
            if (isValidPlacement(grid, row, col, v))
                count++;
        }
        if (count === 0)
            return -2; // Dead end — no candidates
        if (count < bestCount) {
            bestCount = count;
            bestCell = i;
            if (bestCount === 1)
                break; // Can't do better
        }
    }
    return bestCell;
}
/**
 * Recursively solves the grid in-place.
 *
 * @param grid     Mutable grid to solve.
 * @param maxSols  Stop after finding this many solutions (use 1 for fast solve,
 *                 2 to check uniqueness).
 * @returns        Number of solutions found (up to maxSols).
 */
function solve(grid, maxSols, found = { count: 0 }) {
    const cell = nextCell(grid);
    if (cell === -1) {
        // No empty cells — solution found
        found.count++;
        return found.count;
    }
    if (cell === -2) {
        // Dead end
        return found.count;
    }
    const row = Math.floor(cell / 9);
    const col = cell % 9;
    const candidates = [];
    for (let v = 1; v <= 9; v++) {
        if (isValidPlacement(grid, row, col, v))
            candidates.push(v);
    }
    for (const v of candidates) {
        grid[cell] = v;
        solve(grid, maxSols, found);
        if (found.count >= maxSols) {
            return found.count; // Early exit
        }
        grid[cell] = 0;
    }
    return found.count;
}
/**
 * Returns a solved copy of the given grid, or null if unsolvable.
 */
export function solveGrid(grid) {
    const copy = [...grid];
    const count = solve(copy, 1);
    return count === 1 ? copy : null;
}
/**
 * Returns true if the puzzle has exactly one solution.
 * Slightly more expensive than solveGrid — runs until it finds 2 solutions or
 * exhausts the search tree.
 */
export function hasUniqueSolution(grid) {
    const copy = [...grid];
    return solve(copy, 2) === 1;
}
/**
 * Returns the number of solutions (capped at `cap`, default 2).
 * Use for testing / diagnostics.
 */
export function countSolutions(grid, cap = 2) {
    const copy = [...grid];
    return solve(copy, cap);
}
